/**
 * Site content.
 *
 * `cover` points at a real screenshot of the shipped product. Projects that
 * are internal tooling or under NDA have no cover and render as a typographic
 * plate instead. We never fabricate a product shot for work we cannot show.
 */

const data = {
  personal: {
    name: 'Damilare',
    fullName: 'Damilare Oyedeji',
    role: 'Software Engineer',
    tagline: 'Scalable systems. Fluid interfaces.',
    location: 'Lagos, Nigeria',
    timezone: 'WAT (GMT+1)',
    email: 'damoye81@gmail.com',
    phone: '+234 70 312 03859',
    handle: '@fuglydami',
    available: true,
  },

  hero: {
    headline: ['Damilare', 'Oyedeji'],
    subheadline: 'Software Engineer',
    intro: 'I build scalable systems and the interfaces that sit on top of them.',
  },

  about: {
    statement: `I'm a software engineer who builds things that last. Six years across fintech, edtech, SaaS and mobile, shipping products used by hundreds of thousands of people and the infrastructure the teams behind them rely on.`,
    philosophy: `Good software gets out of the way. My approach pairs technical rigour with user-centered thinking, so what ships feels effortless and scales without drama.`,
    focus: [
      'Frontend Development',
      'Mobile Apps (React Native)',
      'Backend & APIs',
      'System Architecture',
      'Design Systems',
      'Developer Tooling',
    ],
    /* `weight` drives the stacked bar widths in the hero. It is a visual
       ranking only, not a second data point, so it is never printed. */
    stats: [
      { value: '20+', label: 'Projects delivered',   weight: 1,    tone: 'coral' },
      { value: '6+',  label: 'Years engineering',    weight: 0.79, tone: 'pine'  },
      { value: '4',   label: 'Companies shipped for', weight: 0.6, tone: 'peach' },
      { value: '1',   label: 'Design system, country wide', weight: 0.43, tone: 'plum' },
    ],
  },

  /* Breadth strip. Deliberately not a list component: these need
     recognition, not individual attention. */
  stack: [
    'React', 'TypeScript', 'Node.js', 'React Native', 'Next.js',
    'GraphQL', 'PostgreSQL', 'MongoDB', 'AWS', 'Docker', 'Expo', 'Storybook',
  ],

  selectedWork: [
    {
      id: 1,
      title: 'Hobwise',
      category: 'Hospitality SaaS',
      description: 'All-in-one platform for restaurant operations covering menus, payments, reservations and campaigns. Built the full frontend architecture from scratch.',
      tech: ['React', 'TypeScript', 'Node.js', 'MongoDB'],
      link: 'https://www.hobwise.com/',
      cover: '/assets/img/hobwise.jpg',
      year: '2024',
      role: 'Lead Frontend Engineer',
    },
    {
      id: 2,
      title: 'Quickteller Paypoint',
      category: 'Fintech Platform',
      description: 'Consumer services platform handling payments, transfers and financial services across Nigeria.',
      tech: ['React', 'TypeScript', 'Node.js'],
      link: 'https://paypoint.quickteller.com/',
      cover: '/assets/img/quickteller-paypoint.png',
      year: '2023',
      role: 'Frontend Engineer',
    },
    {
      id:3,
      title: 'LAUTECH Distance Learning',
      category: 'EdTech',
      description: 'Education management system built to carry remote learning for a full university intake.',
      tech: ['React', 'Node.js', 'MongoDB'],
      link: 'https://lodlc.lautech.edu.ng/portal/',
      cover: '/assets/img/LODLC.png',
      year: '2023',
      role: 'Fullstack Engineer',
    },
    {
      id: 4,
      title: 'FirstBank QR Payments',
      category: 'Banking',
      description: 'QR retail payment flow for First Bank subsidiaries, built for fast and reliable checkout.',
      tech: ['React', 'Node.js', 'QR APIs'],
      link: 'https://yapp-admin-frontend-2.azurewebsites.net/',
      cover: '/assets/img/firstbank-admin.png',
      year: '2022',
      role: 'Frontend Engineer',
    },
       {
      id: 5,
      title: 'QPay',
      category: 'Fintech Platform',
      description: 'Full rebuild of the Quickteller Point POS app using an AI-assisted design-to-code workflow, which cut delivery time and tightened UI consistency.',
      tech: ['React Native', 'TypeScript', 'AI Workflow', 'Expo'],
      link: null,
      cover: '/assets/img/qpay.jpg',
      year: '2025',
      role: 'Lead Frontend Engineer',
    },
        {
      id: 6,
      title: 'Analytics SDK',
      category: 'Developer Tooling',
      description: 'Analytics orchestrator bridging MoEngage, Firebase and Mixpanel. Product teams integrate once and reach every vendor, which removed redundant work across five squads.',
      tech: ['TypeScript', 'Node.js', 'MoEngage', 'Firebase', 'Mixpanel'],
      link: null,
      cover: null,
      year: '2024',
      role: 'Lead Engineer',
    },
    {
      id: 7,
      title: 'Design System',
      category: 'Design Infrastructure',
      description: 'Country-wide design system adopted across multiple products. Standardised components, design tokens, accessibility rules and Storybook documentation.',
      tech: ['React', 'TypeScript', 'Storybook', 'Figma Tokens'],
      link: null,
      cover: null,
      year: '2024',
      role: 'Lead Engineer',
    },
  ],

  contact: {
    headline: 'Have something worth building?',
    message: 'I take on a small number of projects at a time. Tell me what you are working on and I will tell you honestly whether I am the right person for it.',
    /* CTA INTENT LOCK: "Get in touch" is the one label for navigating to
       contact, used in the hero and nowhere else competing with it. This
       is the form's own submit action, so it names what the click does
       rather than repeating the navigation label. */
    cta: 'Send message',
  },
};

export const socialLinks = {
  github:   'https://github.com/Fuglydami',
  twitter:  'https://twitter.com/blackkolanut',
  linkedin: 'https://www.linkedin.com/in/DamilareOyedeji/',
};

export const navLinks = [
  { id: 'about',   label: 'About'   },
  { id: 'work',    label: 'Work'    },
  { id: 'blog',    label: 'Blog'    },
  { id: 'contact', label: 'Contact' },
];

export default data;
